import { DataLoader } from 'src/app/services/data-loader/data-loader';
import {
    DataLoaderResult,
    DataLoaderResultStatus,
} from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import {
    IQueryContext,
    QueryContextDefaultSettings,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import {
    ICompiledSQLTokens,
    generateTableFullPath,
} from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardEngagement } from '../sql-maps/bivz-engagement-sessions-with-dimensions-sql-map';
import {
    DataLoaderUpliftHelper,
    IDataLoaderUpliftHelperOptions,
} from 'src/app/services/data-loader/data-loader-uplift.helper';

export interface EngagementSessionsByDayNoDataLoaderFactoryOptions
    extends
        ICreatedInstanceId,
        IGcpProjectId,
        IQueryCacheService,
        IDataLoaderUpliftHelperOptions {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
    /**
     * Pass callback to retrieve max day no for the query.
     * If not provided, the default max day no will be used.
     * @param context - query context
     * @returns Must return a string with integer value or 'unlimited'
     */
    maxDayNoGetter?: (context: IQueryContext) => string;
}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class EngagementSessionsByDayNoDataLoaderFactory extends Factory<
    DataLoader,
    EngagementSessionsByDayNoDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string =
        'EngagementSessionsByDayNoDataLoaderFactory';

    getInstance(options: EngagementSessionsByDayNoDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap = BiVizDashboardEngagement.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                let getDefaultMaxDayNo = (context: IQueryContext) => {
                    return (
                        queryContext.settings[
                            QueryContextSettingID.ENGAGEMENT_MAX_DAY_NO
                        ] ??
                        QueryContextDefaultSettings[
                            QueryContextSettingID.ENGAGEMENT_MAX_DAY_NO
                        ]!
                    );
                };

                // TODO add context settings. Remember to update chart options build logic in order to support dynamic choice of day_no
                const maxDayNo = options.maxDayNoGetter
                    ? options.maxDayNoGetter(queryContext)
                    : getDefaultMaxDayNo(queryContext);

                const selectedDayNoSQL:
                    | Partial<ICompiledSQLTokens>
                    | undefined =
                    maxDayNo !== 'unlimited'
                        ? {
                              where: [`day_no <= ${maxDayNo}`],
                          }
                        : undefined;

                const {
                    dateTrunc,
                    splitSelect,
                    splitGroupBy,
                    where,
                    // is_in_context_app_version,
                    // unnestDatasourceColumn,
                    join,
                } = BiVizDashboardEngagement.compileSQLStatements(
                    dsColumns.present,
                    queryContext.settings,
                    options,
                    selectedDayNoSQL
                );

                // const selectFacts = is_in_context_app_version
                //     ? 'ds.date, ds.proper_id, app_version.*,'
                //     : 'ds.*,';

                const query = `
-- DataLoaderId: ${this.instanceId}

SELECT 
  ${splitSelect}
  day_no AS day_no,
  APPROX_QUANTILES(duration_minutes, 1000)[OFFSET(500)] AS median_sess_length_mins,
  COUNT(session_no)/COUNT(DISTINCT player_id) AS avg_sess_per_user,
FROM \`${generateTableFullPath(
                    options.gcpProjectId,
                    datasourceSqlMap.dataset,
                    datasourceSqlMap.tableId
                )}\` ds
${join}
${where}
GROUP BY ${splitGroupBy}day_no
ORDER BY ${splitGroupBy}day_no ASC`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);

                DataLoaderUpliftHelper.processUpliftMetrics(
                    options,
                    queryContext,
                    data
                );

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
